import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Visibility as ViewIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  CheckCircle as DeliveredIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import Nav1 from '../components/Nav1';
import Footer from '../components/Footer';
import supabase from '../supabaseClient';

const OrdersContainer = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#fefefe',
  display: 'flex',
  flexDirection: 'column',
});

const ContentContainer = styled(Box)({
  flex: 1,
  padding: '2rem 0',
});

const StyledTableCell = styled(TableCell)({
  color: '#fff',
  borderColor: 'rgba(255, 255, 255, 0.1)',
});

const StyledTableRow = styled(TableRow)({
  '&:hover': {
    backgroundColor: 'rgba(255, 215, 0, 0.1)',
  },
});

const getStatusIcon = (status) => {
  switch (status.toLowerCase()) {
    case 'delivered':
      return <DeliveredIcon sx={{ color: '#4CAF50' }} />;
    case 'shipping':
      return <ShippingIcon sx={{ color: '#2196F3' }} />;
    case 'pending':
      return <PaymentIcon sx={{ color: '#FFD700' }} />;
    default:
      return null;
  }
};

const getStatusColor = (status) => {
  switch (status.toLowerCase()) {
    case 'delivered':
      return '#4CAF50';
    case 'shipping':
      return '#2196F3';
    case 'pending':
      return '#FFD700';
    default:
      return '#fff';
  }
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('No user found');

      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OrdersContainer>
      <Nav1 />
      
      <ContentContainer>
        <Container maxWidth="lg">
          <Typography 
            variant="h4" 
            sx={{ 
              color: '#FFD700', 
              mb: 4,
              fontWeight: 'bold'
            }}
          >
            My Orders
          </Typography>

          <TableContainer 
            component={Paper} 
            sx={{ 
              backgroundColor: '#1a1a1a',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Order ID</StyledTableCell>
                  <StyledTableCell>Date</StyledTableCell>
                  <StyledTableCell>Items</StyledTableCell>
                  <StyledTableCell>Total</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell align="right">Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <StyledTableRow>
                    <StyledTableCell colSpan={6} align="center">
                      Loading orders...
                    </StyledTableCell>
                  </StyledTableRow>
                ) : orders.length === 0 ? (
                  <StyledTableRow>
                    <StyledTableCell colSpan={6} align="center">
                      No orders found
                    </StyledTableCell>
                  </StyledTableRow>
                ) : (
                  orders.map((order) => (
                    <StyledTableRow key={order.id}>
                      <StyledTableCell>
                        #{order.id.slice(0, 8).toUpperCase()}
                      </StyledTableCell>
                      <StyledTableCell>
                        {format(new Date(order.created_at), 'MMM dd, yyyy')}
                      </StyledTableCell>
                      <StyledTableCell>
                        {order.items?.length || 0} items
                      </StyledTableCell>
                      <StyledTableCell>
                        ${order.total?.toFixed(2)}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Chip
                          icon={getStatusIcon(order.status)}
                          label={order.status}
                          sx={{
                            color: getStatusColor(order.status),
                            borderColor: getStatusColor(order.status),
                            backgroundColor: 'transparent',
                            border: '1px solid'
                          }}
                          variant="outlined"
                        />
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <IconButton 
                          size="small"
                          sx={{ 
                            color: '#FFD700',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 215, 0, 0.1)'
                            }
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </ContentContainer>

      <Footer />
    </OrdersContainer>
  );
};

export default Orders; 